/**
 * Conversation Input Footer Component
 * Text input area with send button and quick action buttons
 */

import React, { useState } from 'react';
import { IonFooter, IonRow, IonCol, IonButton, IonIcon, IonSegment, IonSegmentButton, IonLabel, IonGrid, IonTextarea, IonChip, IonSpinner, IonBackdrop } from '@ionic/react';
import { send, attachOutline, closeCircle, alertCircle, mailOutline, chatbubbleOutline, chatboxEllipsesOutline } from 'ionicons/icons';
import { LuMessageSquareReply } from "react-icons/lu";
import styles from '../../pages/Inbox.module.scss';
import { useTranslation } from 'react-i18next';
import { useKeyboardHeight, KEYBOARD_GAP_PX } from '../../hooks/useKeyboardHeight';

/** A file chosen for the email being written, as the footer shows it */
export interface FooterAttachment {
  key: string;
  name: string;
  sizeLabel: string;
  status: 'uploading' | 'ready' | 'error';
  /** Why it can't be sent, when status is 'error' */
  error?: string;
}

interface ConversationInputFooterProps {
  message: string;
  onMessageChange: (text: string) => void;
  onSendClick: () => void;
  /** The "+" button: saved replies */
  onAddClick: () => void;
  /** The paperclip: pick files to attach */
  onAttachClick: () => void;
  /** The files chosen for the email being written */
  attachments?: FooterAttachment[];
  onRemoveAttachment?: (key: string) => void;
  /** Attachments go with emails only */
  attachDisabled?: boolean;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email';
  onChannelChange: (channel: 'chat' | 'email' | 'sms') => void;
  isSending?: boolean;
  canSend?: boolean;
  wrapperClassName?: string;
  subject?: string;
  onSubjectChange?: (text: string) => void;
  hasMessages?: boolean;
  /** Subject of the thread's latest email: the reply's subject starts as "Re: <this>" */
  replySubject?: string;
  fromEmail?: 'scheduling' | 'billing' | 'support';
  onFromEmailChange?: (account: 'scheduling' | 'billing' | 'support') => void;
  /** Called when the on-screen keyboard height changes, so the list above can leave room for it */
  onKeyboardHeightChange?: (height: number) => void;
}

export const ConversationInputFooter: React.FC<ConversationInputFooterProps> = ({
  message,
  onMessageChange,
  onSendClick,
  onAddClick,
  onAttachClick,
  attachments = [],
  onRemoveAttachment,
  attachDisabled = false,
  channel,
  onChannelChange,
  isSending = false,
  canSend = true,
  wrapperClassName = styles.conversationFooter,
  subject = '',
  onSubjectChange,
  hasMessages = false,
  replySubject,
  fromEmail = 'scheduling',
  onFromEmailChange,
  onKeyboardHeightChange,
}) => {
  const { t } = useTranslation();
  const isEmailChannel = ['email', 'gmail', 'outlook', 'yahoo', 'other-email'].includes(channel);
  const [showEmailAccounts, setShowEmailAccounts] = useState(false);

  // The footer is pinned to the bottom of the screen, which the keyboard covers on the native app
  // (the screen isn't resized). Lift it by the keyboard's height, like the search bars do.
  const keyboardHeight = useKeyboardHeight();
  React.useEffect(() => {
    onKeyboardHeightChange?.(keyboardHeight);
  }, [keyboardHeight, onKeyboardHeightChange]);

  // Hiding the email-account picker when the channel stops being email is
  // computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as a setState call inside the effect
  // below, which now only handles the actual external-system notification
  // (onSubjectChange).
  const [prevIsEmailChannel, setPrevIsEmailChannel] = useState(isEmailChannel);
  if (prevIsEmailChannel !== isEmailChannel) {
    setPrevIsEmailChannel(isEmailChannel);
    if (!isEmailChannel) setShowEmailAccounts(false);
  }

  // The subject starts as "Re: <subject of the thread's latest email>" (or the generic line when the thread has no email
  // yet). It is filled again if the thread's emails load after it was first filled, but never over text the person typed.
  const autoSubjectRef = React.useRef('');
  React.useEffect(() => {
    if (!isEmailChannel || !onSubjectChange) return;
    if (subject && subject !== autoSubjectRef.current) return;
    const base = 'R&S Maids Service LLC sent you a new message';
    const next = replySubject
      ? (/^re:/i.test(replySubject.trim()) ? replySubject.trim() : `Re: ${replySubject.trim()}`)
      : (hasMessages ? `Re: ${base}` : base);
    if (next !== subject) {
      autoSubjectRef.current = next;
      onSubjectChange(next);
    }
  }, [isEmailChannel, subject, onSubjectChange, hasMessages, replySubject]);

  const hasReadyAttachment = attachments.some(a => a.status === 'ready');
  const isUploading = attachments.some(a => a.status === 'uploading');

  const fromEmailLabel = fromEmail === 'billing' ? t('components.chatComponents.ConversationInputFooter.billing') : fromEmail === 'support' ? t('components.chatComponents.ConversationInputFooter.support') : t('components.chatComponents.ConversationInputFooter.scheduling');

  return (
    <IonFooter
      className={wrapperClassName}
      style={keyboardHeight > 0 ? { bottom: `${keyboardHeight + KEYBOARD_GAP_PX}px` } : undefined}
    >
      {/* Dims the page behind the account picker. It lives in the footer's own stacking context (the footer is fixed), under the
          input card, so it covers the screen wherever the thread is shown (page or overlay). */}
      {showEmailAccounts && (
        <IonBackdrop className={styles.emailAccountsBackdrop} visible tappable stopPropagation onIonBackdropTap={() => setShowEmailAccounts(false)} />
      )}
      <div className={styles.conversationInputWrapper}>
        <IonGrid className={styles.conversationGrid}>
          {/* Subject Row - only for email channels */}
          {isEmailChannel && (
            <IonRow className={`${styles.subjectRowGrid}${showEmailAccounts ? ` ${styles.blurredBehindPicker}` : ''}`}>
              <IonCol>
                <div className={styles.subjectInputWrapper}>
                  <span className={styles.subjectLabel}>{t('components.chatComponents.ConversationInputFooter.subject')}</span>
                  <input
                    type="text"
                    value={subject}
                    placeholder={t('components.chatComponents.ConversationInputFooter.enterSubject')}
                    onChange={e => onSubjectChange?.(e.target.value)}
                    className={styles.subjectInputField}
                  />
                </div>
              </IonCol>
            </IonRow>
          )}

          {/* The files chosen for this email */}
          {attachments.length > 0 && (
            <IonRow className={styles.attachmentRowGrid}>
              <IonCol>
                {attachments.map(a => (
                  <IonChip key={a.key} color={a.status === 'error' ? 'danger' : undefined} title={a.error}>
                    {a.status === 'uploading' && <IonSpinner name="dots" />}
                    {a.status === 'error' && <IonIcon icon={alertCircle} />}
                    <IonLabel>{a.name} · {a.error || a.sizeLabel}</IonLabel>
                    <IonIcon
                      icon={closeCircle}
                      aria-label={t('components.chatComponents.ConversationInputFooter.removeAttachment')}
                      onClick={() => onRemoveAttachment?.(a.key)}
                    />
                  </IonChip>
                ))}
              </IonCol>
            </IonRow>
          )}

          {/* Input Row */}
          <IonRow className={`${styles.inputRowGrid} ion-align-items-center${showEmailAccounts ? ` ${styles.blurredBehindPicker}` : ''}`}>
            <IonCol className={styles.inputColTextarea}>
              <IonTextarea
                value={message}
                placeholder={t('components.chatComponents.ConversationInputFooter.typeYourMessage')}
                onIonInput={e => onMessageChange(e.detail.value || '')}
                className={`${styles.messageInputField} chat-textarea`}
                autoGrow={true}
                rows={1}
              />
              <IonIcon
                icon={send}
                className={`${styles.sendButtonAction} chat-send-icon`}
                onClick={() => { if (canSend && (message.trim() || hasReadyAttachment) && !isUploading && !isSending) onSendClick(); }}
              />
            </IonCol>
          </IonRow>

          {/* Channel + Actions Row */}
          <IonRow className={styles.buttonsRowGrid}>
            <IonCol className={styles.channelCol}>
              <IonSegment
                value={isEmailChannel ? 'email' : channel}
                onIonChange={(e) => {
                  const val = e.detail.value as string;
                  const current = isEmailChannel ? 'email' : channel;
                  if (val === current) return;
                  if (val !== 'email') {
                    onChannelChange(val as 'chat' | 'sms');
                    setShowEmailAccounts(false);
                  }
                }}
                className={styles.channelSegmentButtons}
              >
                <IonSegmentButton value="chat" layout="icon-top">
                  <IonIcon icon={chatbubbleOutline} />
                  <IonLabel>{t('components.chatComponents.ConversationInputFooter.chat')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="email" layout="icon-top" onClick={() => {
                  if (!isEmailChannel) onChannelChange('email');
                  setShowEmailAccounts(prev => !prev);
                }}>
                  <IonIcon icon={mailOutline} />
                  <IonLabel>{isEmailChannel ? fromEmailLabel : t('components.chatComponents.ConversationInputFooter.email')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="sms" layout="icon-top">
                  <IonIcon icon={chatboxEllipsesOutline} />
                  <IonLabel>{t('components.chatComponents.ConversationInputFooter.sms')}</IonLabel>
                </IonSegmentButton>
              </IonSegment>

              {showEmailAccounts && (
                <div className={styles.emailProviderRow}>
                  {(['scheduling', 'billing', 'support'] as const).map(account => (
                    <button
                      key={account}
                      className={`${styles.emailProviderChip} ${fromEmail === account ? styles.emailProviderChipActive : ''}`}
                      onClick={() => { onFromEmailChange?.(account); localStorage.setItem('lastUsedFromEmail', account); setShowEmailAccounts(false); }}
                    >
                      {account.charAt(0).toUpperCase() + account.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </IonCol>

            <IonCol size="auto">
              <IonButton fill="clear" className={styles.footerActionButton} onClick={onAddClick}>
                <LuMessageSquareReply size={30} strokeWidth={1.5} />
              </IonButton>
            </IonCol>
            <IonCol size="auto">
              <IonButton
                fill="clear"
                className={styles.footerActionButton}
                onClick={onAttachClick}
                disabled={attachDisabled}
                title={attachDisabled ? t('components.chatComponents.ConversationInputFooter.attachEmailOnly') : t('components.chatComponents.ConversationInputFooter.attachFiles')}
              >
                <IonIcon icon={attachOutline} slot="icon-only" />
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
    </IonFooter>
  );
};
